import {
    List, Datagrid, TextField, DateField, ReferenceField,
    Edit, Create, DateInput, BooleanInput, ReferenceInput, SelectInput,
    TabbedForm, FormTab, useRecordContext, useTranslate, TopToolbar, ListButton,
} from 'react-admin';
import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/material';
import { APPLICATION_CHOICES } from './roles';

// SKU + Anwendung anzeigen, damit "Azubiweb" von den Academy-Tiers
// (Basic/Professional/Enterprise) unterscheidbar bleibt.
const productOptionText = (record: any) => {
    const app = APPLICATION_CHOICES.find((c) => c.id === record?.application)?.name;
    return app ? `${record.name} (${app})` : record?.name;
};

export const LicenseList = () => (
    <List>
        <Datagrid rowClick="edit">
            <TextField source="id" />
            <ReferenceField source="company" reference="companies" link="show">
                <TextField source="displayName" />
            </ReferenceField>
            <ReferenceField source="product" reference="products" link="show">
                <TextField source="name" />
            </ReferenceField>
            <DateField source="startDate" />
            <DateField source="expiresAt" emptyText="Unbefristet" />
            <LicenseStatusChip label="resources.licenses.fields.status" />
            <DateField source="createdAt" showTime />
        </Datagrid>
    </List>
);

const LicenseTitle = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    return <span>{record ? `${translate('resources.licenses.name', { smart_count: 1 })} #${record.id}` : ''}</span>;
};

export const isLicenseExpired = (expiresAt: string, now: Date = new Date()): boolean => {
    const [year, month, day] = expiresAt.slice(0, 10).split('-').map(Number);
    return now >= new Date(year, month - 1, day + 1);
};

export const LicenseStatusChip = (_props: { label?: string }) => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    const isExpired = record.expiresAt && isLicenseExpired(record.expiresAt);
    const isLifetime = !record.expiresAt;

    if (!record.active) {
        return <Chip label={translate('resources.licenses.status.inactive')} color="error" size="small" />;
    }
    if (isExpired) {
        return <Chip label={translate('resources.licenses.status.expired')} color="warning" size="small" />;
    }
    if (isLifetime) {
        return <Chip label={translate('resources.licenses.status.lifetime')} color="success" size="small" />;
    }
    return <Chip label={translate('resources.licenses.status.active')} color="success" size="small" />;
};

const MetadataCard = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translate('resources.licenses.tabs.metadata')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {translate('resources.licenses.fields.createdAt')}
                        </Typography>
                        <Typography variant="body2">
                            {record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {translate('resources.licenses.fields.updatedAt')}
                        </Typography>
                        <Typography variant="body2">
                            {record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {translate('resources.licenses.fields.status')}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <LicenseStatusChip />
                        </Box>
                    </Grid>
                    {record.uuid && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                UUID
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {record.uuid}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};

export const LicenseEdit = () => {
    const translate = useTranslate();

    return (
        <Edit title={<LicenseTitle />} actions={<TopToolbar><ListButton /></TopToolbar>}>
            <TabbedForm>
                <FormTab label={translate('resources.licenses.tabs.general')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <ReferenceInput source="company" reference="companies" sort={{ field: 'name', order: 'ASC' }}>
                                    <SelectInput optionText="displayName" fullWidth />
                                </ReferenceInput>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <ReferenceInput source="product" reference="products" sort={{ field: 'name', order: 'ASC' }}>
                                    <SelectInput optionText={productOptionText} fullWidth />
                                </ReferenceInput>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DateInput
                                    source="startDate"
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DateInput
                                    source="expiresAt"
                                    fullWidth
                                    helperText={translate('resources.licenses.fields.expiresAtHelp')}
                                />
                            </Grid>
                        </Grid>
                        <BooleanInput source="active" />
                        <MetadataCard />
                    </Box>
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

export const LicenseCreate = () => {
    const translate = useTranslate();

    return (
        <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
            <TabbedForm>
                <FormTab label={translate('resources.licenses.tabs.general')}>
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <ReferenceInput source="company" reference="companies" sort={{ field: 'name', order: 'ASC' }}>
                                    <SelectInput optionText="displayName" fullWidth required />
                                </ReferenceInput>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <ReferenceInput source="product" reference="products" sort={{ field: 'name', order: 'ASC' }}>
                                    <SelectInput optionText={productOptionText} fullWidth required />
                                </ReferenceInput>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DateInput
                                    source="startDate"
                                    fullWidth
                                    defaultValue={new Date().toISOString().split('T')[0]}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DateInput
                                    source="expiresAt"
                                    fullWidth
                                    helperText={translate('resources.licenses.fields.expiresAtHelp')}
                                />
                            </Grid>
                        </Grid>
                        <BooleanInput source="active" defaultValue={true} />
                    </Box>
                </FormTab>
            </TabbedForm>
        </Create>
    );
};
